import type { ReactElement, ReactNode } from 'react';
import { SearchField as AriaSearchField, type SearchFieldProps } from 'react-aria-components';
import { useIntl } from 'react-intl';
import { Button } from '#components/button/button';
import { FieldLabel } from '#components/form-elements/label/label';
import { Spinner } from '#components/spinner/spinner';
import IconCrossOutlined from '@repo/monochrome-icons/icon-cross-outlined';
import IconSearchOutlined from '@repo/monochrome-icons/icon-search-outlined';

export interface SearchFieldBaseProps extends Omit<SearchFieldProps, 'children'> {
  placeholder: string;
  /** The input element to render (e.g. DSK `Input` for the regular variant, RAC `Input` for toolbar). */
  inputSlot: ReactNode;
  /** Icon rendered inside the clear button. Defaults to `IconCrossOutlined`. */
  closeIcon?: ReactNode;
  iconClassName?: string;
  closeClassName?: string;
  /** Shows a loading spinner (positioned absolutely by the consuming variant's CSS). */
  showLoading?: boolean;
  spinnerClassName?: string;
  /**
   * Wraps the icon/input/clear/spinner row. Required to add `label`: it stacks above this row, so
   * the row's own position:relative anchoring for the absolutely positioned icon/clear/spinner has
   * to live here rather than on the outer field element.
   */
  rowClassName?: string;
  label?: string | ReactNode;
}

export function SearchFieldBase({
  placeholder,
  className,
  inputSlot,
  closeIcon = <IconCrossOutlined />,
  iconClassName,
  closeClassName,
  showLoading,
  spinnerClassName,
  rowClassName,
  label,
  ...props
}: SearchFieldBaseProps): ReactElement {
  const intl = useIntl();
  const clearLabel = intl.formatMessage({ id: 'a11y-buttons-clear-search-aria-label' });

  return (
    <AriaSearchField aria-label={label ? undefined : placeholder} className={className} {...props}>
      {label ? <FieldLabel>{label}</FieldLabel> : null}
      <span className={rowClassName}>
        <IconSearchOutlined className={iconClassName} />
        {inputSlot}
        <Button
          aria-label={clearLabel}
          className={closeClassName}
          data-test-search-input-delete
          iconOnly
          size="small"
          variant="tertiary"
        >
          {closeIcon}
        </Button>
        {showLoading ? <Spinner className={spinnerClassName} /> : null}
      </span>
    </AriaSearchField>
  );
}
